import React, { FC, useState, useEffect, useCallback } from 'react';
import LocalModal from 'components/base/local-modal';
import { ReactComponent as ListIcon } from 'assets/icons/list-icon.svg';
import { Button } from '@cokitchen/cokitchen-components';
import CsvIcon from 'assets/images/csv.png';
import CsvInfoModal from '../../../../../components/modals/csv-info-modal';
import { useNavigate } from 'react-router-dom';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import usePricingContext from 'contexts/pricing.context';
import { DefaultconversionListFormData } from '../add-configuration';
import * as XLSX from 'xlsx';
import { IngredientType, MealPricingType, MealXlsType } from 'models/pricing.model';
import { apiService } from 'services/base.service';
import { handleApiError } from 'utils/functions';
type AddVendorProps = {
  show: boolean;
  onClose: () => void;
};

const VendorAdditionDetails = {
  subtitle:
    'Welcome to the Vendor addition process via CSV file. Please Follow these steps to ensure a smooth member addition:',
  steps: [
    {
      number: 1,
      heading: 'Download the Template',
      description: "Click the 'Download CSV Template' button to obtain the template file."
    },
    {
      number: 2,
      heading: "Fill in vendors' Details",
      description: ' Open the downloaded CSV file, Specify the role of each vendor.'
    },
    {
      number: 3,
      heading: 'Save Your Changes',
      description: 'Save the CSV file with your entries'
    },
    {
      number: 4,
      heading: 'Review',
      description:
        'Crosscheck the details in the CSV file. Click confirm to select the file and upload'
    },
    {
      number: 5,
      heading: 'Verify the details',
      description:
        'After uploading, the details will be displayed for final review before addition,then click continue to add members to the company'
    }
  ]
};
const AddVendorModal: FC<AddVendorProps> = ({ show, onClose }) => {
  const navigate = useNavigate();
  const [showCsvInfoModal, setShowCsvInfoModal] = useState(false);
  const { active, setActive, setConversionListFormData, setStep, setSelectedConversionItem } =
    usePricingContext();
  const [file, setFile] = useState<File | null>(null);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const toggleCsvInfoModal = () => {
    setShowCsvInfoModal(!showCsvInfoModal);
  };
  const [ingredients, setIngredients] = useState<IngredientType[]>([]);
  const [ingredientMixes, setIngredientMixes] = useState<MealPricingType[]>([]);

  const PAGE_SIZE = 50;
  const fetchStoreItems = async (page: number) => {
    try {
      const response = await apiService.get(`ingredients`, {
        params: { page_size: PAGE_SIZE, page }
      });
      if (response) {
        setIngredients((prev) => [...prev, ...response.data.results]);
        return response.data.total;
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const fetchAllStoreItems = useCallback(async () => {
    const total = await fetchStoreItems(1);
    const totalPages = Math.ceil(total / PAGE_SIZE);

    for (let page = 2; page <= totalPages; page++) {
      await fetchStoreItems(page);
    }
  }, []);
  const fetchMixItems = async (page: number) => {
    try {
      const response = await apiService.get(`ingredient-mix`, {
        version: 'v2',
        params: { page_size: 50, page, type: 'prep' }
      });
      if (response.data) {
        setIngredientMixes((prev) => [...prev, ...response.data.data]);
        return response.data.pagination.total;
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const fetchAllMixItems = useCallback(async () => {
    const total = await fetchMixItems(1);
    const totalPages = Math.ceil(total / PAGE_SIZE);

    for (let page = 2; page <= totalPages; page++) {
      await fetchMixItems(page);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setIsFetchingData(true);
      await Promise.all([fetchAllStoreItems(), fetchAllMixItems()]);
      setIsFetchingData(false);
    })();
  }, []);

  const readFile = () => {
    if (file) {
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });

        const formatMealCost = workbook.Sheets;
        const formattedArrayOfMeals = Object.entries(formatMealCost).map((item) =>
          XLSX.utils.sheet_to_json(item[1])
        );
        const fData = formattedArrayOfMeals.map((meal: any, index: number) => {
          const getFoodIngredients = meal
            .map((m: MealXlsType) => {
              if (m['Type'] !== 'food') return null;

              if (m['Mix Type'] === 'ingredient') {
                const findIngredient =
                  ingredients.find(
                    (ings) =>
                      ings?.name?.trim().toLowerCase() === m['Ingredients'].trim().toLowerCase()
                  ) ?? null;
                if (!findIngredient) return null;
                return {
                  ...findIngredient,
                  quantity: m['Qty'],
                  type: 'ingredient',
                  ingredient_name: findIngredient.name,
                  meal_type: 'ingredient'
                };
              }

              if (m['Mix Type'] === 'prep') {
                const findMix =
                  ingredientMixes.find(
                    (mix) =>
                      mix?.ingredient?.name?.trim().toLowerCase() ===
                      m['Ingredients'].trim().toLowerCase()
                  ) ?? null;

                if (!findMix) return null;
                return {
                  ...findMix.ingredient,
                  quantity: m['Qty'],
                  type: 'mix',
                  ingredient_name: findMix.ingredient.name,
                  mix_id: findMix.id,
                  meal_type: 'mix'
                };
              }

              return null;
            })
            .filter((foodIngredient: any) => !!foodIngredient);

          const getPackagingIngredients = meal
            .map((m: MealXlsType) => {
              if (m['Type'] !== 'packaging') return null;

              const findIngredient =
                ingredients.find(
                  (ings) =>
                    ings?.name?.trim().toLowerCase() === m['Ingredients'].trim().toLowerCase()
                ) ?? null;
              if (!findIngredient) return null;
              return { ...findIngredient, quantity: m['Qty'] };
            })
            .filter((foodIngredient: any) => !!foodIngredient);
          return {
            name: meal[0]['Meal'],
            brand: meal[0]['Brand'],
            no_of_portions: meal[0]['Portion'],
            portion_type: 'unit',
            ingredients: [...getFoodIngredients],
            packaging: [...getPackagingIngredients],
            itemIndex: index
          };
        });
        const reducedData = fData.reduce((acc: any[], curr: any) => {
          const existing = acc.find((item) => item.name === curr.name);
          if (existing) {
            existing.ingredients = [
              ...(existing.ingredients || []),
              ...(curr.ingredients ? curr.ingredients.flat() : [])
            ];
          } else {
            acc.push({
              ...curr,
              ingredients: curr.ingredients ? curr.ingredients.flat() : []
            });
          }
          return acc;
        }, []);

        setConversionListFormData(reducedData);
        setSelectedConversionItem(reducedData[0]);
      };
      navigate(MAIN_STORE_ROUTE_KEYS.ADD_MEALS_CONFIGURATION);
      setStep(2);
    }
  };

  useEffect(() => {
    if (!file || isFetchingData) return;
    readFile();
  }, [file, isFetchingData]);

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    setActive('csv');
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files) setFile(files[0]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };
  const handleFileUpload = (e: any) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };
  const reset = () => {
    setActive('csv');
    setConversionListFormData([]);
  };

  return (
    <LocalModal
      isOpen={show}
      onRequestClose={() => {
        onClose();
        setActive(null);
      }}
      title='Add new meal'
      contentClassName='!max-w-[442px]'>
      <div className='flex gap-4 my-4 w-full '>
        <div
          onClick={() => {
            setActive('csv');
            toggleCsvInfoModal();
          }}
          onDrop={handleFileDrop}
          onDragOver={handleDragOver}
          className={`${
            active === 'csv' ? 'border-[#E60A2B] border-[1.5px]' : 'border-[#EAEBEB]'
          } border w-full sm:w-[185px] h-[139px]  hover:border-[#E60A2B] cursor-pointer hover:border-[1.5px] p-4 rounded-md space-y-3`}>
          <div className='border border-[#EAEBEB] p-2 rounded-full w-9 h-9 flex items-center'>
            <img src={CsvIcon} alt='' />
          </div>
          <h2 className='text-sm font-semibold'>Add via .csv</h2>
          <p className='text-[#6F7376] text-xs font-normal'>Add multiple meals configuration</p>
        </div>

        <div
          onClick={() => {
            setActive('manual');
            setConversionListFormData([DefaultconversionListFormData]);
            setStep(1);
          }}
          className={`${
            active === 'manual' ? 'border-[#E60A2B] border-[1.5px]' : 'border-[#EAEBEB] '
          } border w-full sm:w-[185px] h-[139px] hover:border-[#E60A2B] cursor-pointer hover:border-[1.5px] p-4 rounded-md space-y-3`}>
          <div className='border border-[#EAEBEB] p-2 rounded-full w-9 h-9 flex items-center'>
            <ListIcon />
          </div>
          <h2 className='text-sm font-semibold'>Add Manually</h2>
          <p className='text-[#6F7376] text-xs font-normal whitespace-nowrap'>
            Add meals manually to the <br /> list
          </p>
        </div>
      </div>
      <div className='flex justify-end mt-6 '>
        <Button
          color='transparent'
          variant='block'
          small
          className='w-[139px] '
          onClick={() => {
            onClose();
            setActive(null);
          }}>
          Cancel
        </Button>
        <Button
          color='primary'
          variant='block'
          small
          className='w-[139px]'
          onClick={() => {
            if (active === 'manual') {
              setConversionListFormData([DefaultconversionListFormData]);
              setStep(1);
            }
            setSelectedConversionItem(null);
            navigate(MAIN_STORE_ROUTE_KEYS.ADD_MEALS_CONFIGURATION);
          }}>
          Confirm
        </Button>
      </div>

      {showCsvInfoModal && (
        <CsvInfoModal
          show={showCsvInfoModal}
          onClose={toggleCsvInfoModal}
          handleFileUpload={handleFileUpload}
          handleFileChange={reset}
          title='Add Meal Configuration via CSV file'
          downloadLink='https://docs.google.com/spreadsheets/d/1AEzotin3VEb6qw1mFHUBrH6roSxwEb27aKwXa_8EuI4/'>
          {file && isFetchingData ? (
            <div className='w-full text-center max-w-[400px] py-6'>
              <p className=' text-[#2E3031] font-medium'>Your file is being processed...</p>
              <p className='text-sm text-[#6F7376] mt-1'>Please wait while we fetch all data.</p>
            </div>
          ) : (
            <div className='w-full max-w-[400px]'>
              <h2 className='text-sm text-[#2E3031]'>{VendorAdditionDetails.subtitle}</h2>
              <div>
                {VendorAdditionDetails.steps.map((step) => (
                  <div className='flex items-center max-w-[400px]' key={step.number}>
                    <div className='inline-block relative'>
                      <span className='w-5 h-5 bg-[#e7e8e9] text-sm rounded-full flex items-center justify-center mb-2 mr-2 p-1'>
                        {step.number}
                      </span>
                    </div>
                    <span className='mt-3'>
                      <h3 className='text-sm font-medium '>{step.heading}</h3>
                      <p className='text-xs'>{step.description}</p>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CsvInfoModal>
      )}
    </LocalModal>
  );
};

export default AddVendorModal;
